import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isStaff, ROLE_LABELS, normRole } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Lock, Mail, ShieldCheck, Eye, EyeOff, Building2 } from "lucide-react";

function errMsg(detail) {
  if (detail == null) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail?.msg || detail);
}

export default function StaffLogin() {
  const navigate = useNavigate();
  const { user, loading, setUser } = useAuth();

  useEffect(() => {
    if (!loading && user) navigate(isStaff(user.role) ? "/admin" : "/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const done = (data) => {
    setUser(data);
    if (isStaff(data.role)) {
      toast.success(`Welcome, ${data.name || data.email} · ${ROLE_LABELS[normRole(data.role)]}`);
      navigate("/admin", { replace: true });
    } else {
      toast.message("This account is not a staff/partner account. Redirecting to your dashboard.");
      navigate("/dashboard", { replace: true });
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0b2a1a]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-emerald-950 text-white p-12 grain relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2.5 relative z-10">
          <img src="/logo.png" alt="NovaAgri" className="h-10 w-10 rounded-lg bg-white/10 object-contain p-0.5" />
          <div>
            <div className="font-head font-extrabold">NovaAgri Solution LLP</div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-300/70">Staff & Partner Portal</div>
          </div>
        </Link>
        <div className="relative z-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-400/15 px-4 py-1.5 text-sm font-semibold text-emerald-200"><ShieldCheck className="h-4 w-4" /> Secure Staff Access</span>
          <h2 className="mt-5 font-head font-extrabold text-4xl leading-tight">Operations, zones & applications — all in one console.</h2>
          <p className="mt-5 text-emerald-100/70 max-w-md">Sign in with your staff or channel-partner account. Access is role-based — you'll see only the modules assigned to you.</p>
          <div className="mt-8 grid grid-cols-2 gap-3 max-w-md text-sm">
            {["Super Admin", "Operations Manager", "Zonal Manager", "Field Executive", "Channel Partner"].map((r) => (
              <div key={r} className="flex items-center gap-2 text-emerald-100/80"><Building2 className="h-3.5 w-3.5 text-emerald-300" /> {r}</div>
            ))}
          </div>
        </div>
        <div className="text-xs text-emerald-200/40 relative z-10">© 2026 NovaAgri Solution LLP</div>
      </div>

      {/* Right auth */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-[#f8faf8]">
        <Card className="w-full max-w-md rounded-3xl border-emerald-100 shadow-xl shadow-emerald-900/10 p-7 sm:p-9">
          <Link to="/" className="flex items-center gap-2 text-sm text-emerald-700 mb-6"><ArrowLeft className="h-4 w-4" /> Back to home</Link>
          <div className="h-11 w-11 rounded-2xl bg-emerald-50 grid place-items-center mb-4"><ShieldCheck className="h-6 w-6 text-emerald-700" /></div>
          <h1 className="font-head font-extrabold text-2xl sm:text-3xl text-emerald-950">Staff & Partner Login</h1>
          <p className="mt-1.5 text-slate-500 text-sm">Sign in to the NovaAgri operations console.</p>

          <div className="mt-6"><PasswordForm onDone={done} /></div>

          <div className="relative mt-6 mb-1 py-3"><div className="border-t border-slate-200" /><span className="absolute inset-x-0 -top-2.5 mx-auto w-fit bg-white px-3 text-xs text-slate-400">or</span></div>
          <GoogleButton />

          <p className="mt-6 text-center text-xs text-slate-400">
            Are you a farmer? <Link to="/login" className="text-emerald-700 font-semibold hover:underline" data-testid="go-farmer-login">Use the farmer login</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}

const GoogleButton = () => {
  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button onClick={signIn} variant="outline" className="w-full rounded-full bg-white hover:bg-slate-50 text-slate-700 border-slate-200 h-12" data-testid="staff-google-login-button">
      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5 mr-3" /> Continue with Google
    </Button>
  );
};

const PasswordForm = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      onDone(res.data);
    } catch (err) {
      toast.error(errMsg(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <Label className="text-slate-600">Work Email</Label>
        <div className="mt-1.5 relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@novaagrisolution.com" className="pl-9 rounded-xl h-11" data-testid="staff-email-input" />
        </div>
      </div>
      <div>
        <Label className="text-slate-600">Password</Label>
        <div className="mt-1.5 relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-9 pr-10 rounded-xl h-11" data-testid="staff-password-input" />
          <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
        </div>
      </div>
      <Button type="submit" disabled={loading} className="w-full rounded-full bg-brand hover:bg-emerald-800 text-white h-11" data-testid="staff-login-button">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign In <ArrowRight className="ml-2 h-4 w-4" /></>}
      </Button>
    </form>
  );
};
